import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { FloorAssemblyHashParameters } from "building-types/floors/floorAssemblyTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import type { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import type { WallEndCap } from "building-types/walls/wallEndsTypes";
import type { StartSortedWallGaps } from "building-types/walls/wallGap";
import type { Extrusion } from "geom/brep";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type {
  FoundationConditionData,
  FoundationAssembly,
  IsolatedFoundationPickSource,
} from "./foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "./foundationComponentEntityTypes";
import type { FoundationCondition, FoundationSubSystemType, FoundationSuperSystemType } from "./foundationEnums";
import type { FoundationSectionSizeType } from "./foundationEnums";
import type { FoundationPathDatums } from "./foundationPathTypes";

export interface Slab {
  readonly id: string;
  readonly size: FoundationExtrusionSize;
  readonly roomClass: RoomClass;
  readonly hashParams: Partial<FloorAssemblyHashParameters>;
  readonly halfEdges: number[];
  readonly planProfile: PolyLine2Polygon;
  readonly extrusion: Extrusion;
  readonly exteriorProfile: PolyLine2Polygon; // to outside face of framing. This does not always represent the slab edge (i.e stemwall)
  readonly planProfileHalfEdges: EdgeData[][][];
  readonly exteriorProfileHalfEdges: EdgeData[][][];
  isSlabDepression: boolean;
}

// Foundation Super Systems

export type FoundationSuperSystem =
  | SlabOnGradeFoundationSuperSystem
  | RaisedSlabFoundationSuperSystem
  | CrawlspaceFoundationSuperSystem
  | BasementFoundationSuperSystem;

interface FoundationSuperSystemBase<T extends FoundationSuperSystemType> {
  readonly type: T;
  readonly slabs: Slab[];
}

type SlabOnGradeFoundationSuperSystem = FoundationSuperSystemBase<FoundationSuperSystemType.SLAB_ON_GRADE>;
type RaisedSlabFoundationSuperSystem = FoundationSuperSystemBase<FoundationSuperSystemType.RAISED_SLAB>;
type CrawlspaceFoundationSuperSystem = FoundationSuperSystemBase<FoundationSuperSystemType.CRAWLSPACE>;
type BasementFoundationSuperSystem = FoundationSuperSystemBase<FoundationSuperSystemType.BASEMENT>;

// Foundation Sub Systems

export type FoundationSubSystem =
  | MonolithicFoundationSubSystem
  | StemWallCMUFoundationSubSystem
  | StemWallCIPFoundationSubSystem
  | IsolatedFoundationSubSystem;

interface FoundationSubSystemBase<T extends FoundationSubSystemType> {
  readonly type: T;
  readonly strips: HalfEdgeToFoundationStrip;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
}

type MonolithicFoundationSubSystem = FoundationSubSystemBase<FoundationSubSystemType.MONOLITHIC>;
type StemWallCMUFoundationSubSystem = FoundationSubSystemBase<FoundationSubSystemType.STEM_WALL_CMU>;
type StemWallCIPFoundationSubSystem = FoundationSubSystemBase<FoundationSubSystemType.STEM_WALL_CIP>;
type IsolatedFoundationSubSystem = FoundationSubSystemBase<FoundationSubSystemType.ISOLATED>;

/**
 * GeneratedFoundationSubSystem contains the foundation sub-system data
 * This is now stored separately in the Building type
 */
export interface GeneratedFoundationSubSystem extends BuildingEntityBase<"FOUNDATION_SYSTEM"> {
  readonly levelId: string;
  readonly planarGraphId: string; //need to add planar graph id as levelId is not always the same as the planar graph id. We use the docEntity handle to get the planar graph id from the foundation  or floor entity.
  readonly settings: FoundationSystemSettings;
  readonly subSystem: FoundationSubSystem;
  readonly perimeter?: PolyLine2;
}

export type GeneratedIntermediateFoundationSubSystem = Omit<GeneratedFoundationSubSystem, "subSystem"> & {
  readonly subSystem: IntermediateFoundationSubSystem;
};

/**
 * GeneratedFoundationHorizontalAssemblySubSystem contains the foundation sub-system data
 * for horizontal assemblies (Foundation entities). This is stored in the foundationHorizontalAssemblies
 * table in Building.
 */
export interface GeneratedFoundationHorizontalAssemblySubSystem
  extends BuildingEntityBase<"FOUNDATION_SYSTEM_HORIZONTAL_ASSEMBLY"> {
  readonly levelId: string;
  readonly planarGraphId: string;
  readonly settings: FoundationSystemSettings;
  readonly subSystem: FoundationSubSystem;
  readonly perimeter: PolyLine2;
}

export type GeneratedFoundationSubSystemAny =
  | GeneratedFoundationSubSystem
  | GeneratedFoundationHorizontalAssemblySubSystem;

/**
 * GeneratedFoundationSuperSystem contains the foundation super-system data
 * This is now stored separately in the Building type
 */
export interface GeneratedFoundationSuperSystem extends BuildingEntityBase<"FOUNDATION_SUPER_SYSTEM"> {
  readonly levelId: string;
  readonly settings: FoundationSystemSettings;
  readonly superSystem: FoundationSuperSystem;
}

// Foundation Components

export type HalfEdgeToFoundationStrip = { [halfEdgeIndex: number]: FoundationStrip };
export type HalfEdgeToFoundationPath = { [halfEdgeIndex: number]: FoundationPath };

export interface FoundationConditionTag {
  readonly strips: FoundationStrip[];
  readonly condition: FoundationCondition;
  readonly data: FoundationConditionData;
  readonly x: number;
  readonly y: number;
  readonly lines: PolyLine2[];
  readonly rotation: number; // in radians
  readonly direction: TagDirection;
  readonly label?: string;
}

export interface FoundationPlanText {
  readonly centerX: number;
  readonly centerY: number;
  readonly header: string;
  readonly detail: string;
}

export enum TagDirection {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

/**
 * @NOTE - datums are extended to be undefined to indicate that the datums are not set
 * from the FoundationPathDatums (actuall FloorPath, see comment above @FoundationPathDatums).
 * This is used to indicate that the datums are not set for a particular foundation path.
 * It is part of decoupling the FloorPath from the FoundationPath so that FoundationPaths
 * may exist and work without the FloorPaths.
 *
 * if isDerivedFromHorizontalAssembly is true, then the FoundationPath is derived from the HorizontalAssembly
 *    in such a case, foundation generation will ignore imposed system constraints when applying the assembly generation
 *    e.g. level constraints, foundation assembly constraints, etc.
 *
 *    if isDerivedFromHorizontalAssembly is false or undefined, then the FoundationPath is derived from the FloorPath
 *    in such a case, foundation generation will apply the system constraints when applying the assembly generation
 *    e.g. level constraints, foundation assembly constraints, etc. This is the legacy behavior.
 *
 */
// Foundation strip types
export interface FoundationPath {
  readonly halfEdgeIndex: number;
  readonly planarGraphId: string; // planarGraphId - (currently levelId) - used to look up the planar graph
  readonly condition: FoundationCondition;
  readonly assembly: FoundationAssembly;
  readonly isolatedAssemblies: FoundationAssembly[];
  readonly datums?: FoundationPathDatums;
  readonly subSystemOffset: number;
  readonly superSystemOffset: number;
  readonly isDerivedFromHorizontalAssembly?: boolean;
}

export interface FoundationExtrusionSize {
  readonly top: number;
  readonly depth: number;
  readonly material?: ExteriorMaterial;
}

export interface FoundationSectionOpeningDescriptor extends FoundationExtrusionSize {
  readonly outline: PolyLine2;
  readonly isIsolatedFoundation?: boolean;
}

export interface LinearFoundationSectionSize extends FoundationSectionSizeBase, FoundationExtrusionSize {
  readonly sectionType: FoundationSectionType;
  readonly sizeType: FoundationSectionSizeType.LINEAR;
  readonly left: number;
  readonly right: number;
  // if the section start is determined to be a freeEnd, this value will be used as the end extension
  readonly start: number;
  // if the section end is determined to be a freeEnd, this value will be used as the end extension
  readonly end: number;
  readonly openings: FoundationSectionOpeningDescriptor[]; // these will be subtracted from all planProfiles and extrusions
}

export interface IsolatedFoundationSectionSize extends FoundationSectionSizeBase, FoundationExtrusionSize {
  readonly sectionType: FoundationSectionType.COMPONENT;
  readonly sizeType: FoundationSectionSizeType.ISOLATED;
  readonly left: number;
  readonly right: number;
  readonly assemblyId: string;
  readonly pickSource?: IsolatedFoundationPickSource;
}

interface FoundationSectionSizeBase {
  readonly sectionComponent: FoundationComponentEntity;
  readonly sectionId: string | undefined;
  readonly sizeType: FoundationSectionSizeType;
}

export type FoundationSectionSize =
  | LinearFoundationSectionSize
  | IsolatedFoundationSectionSize
  | VoidFoundationSectionSize;

interface VoidFoundationSectionSize extends FoundationSectionSizeBase {
  readonly sizeType: FoundationSectionSizeType.VOID;
}

export enum FoundationSectionType {
  COMPONENT = "COMPONENT",
  INSET = "INSET",
  LEDGE = "LEDGE",
}

interface FoundationSectionBase<T extends FoundationSectionSize> {
  readonly id: string;
  readonly size: T;
  readonly planProfiles: PolyLine2[];
  readonly extrusions: Extrusion[];
  readonly path: FoundationPath;
  readonly material: ExteriorMaterial;
}

export interface LinearFoundationSection extends FoundationSectionBase<LinearFoundationSectionSize> {
  readonly startCap: WallEndCap;
  readonly endCap: WallEndCap;
  readonly gapProfiles: PolyLine2[];
  readonly leftEdgeGaps: StartSortedWallGaps;
  readonly rightEdgeGaps: StartSortedWallGaps;
}

export type IsolatedFoundationSection = FoundationSectionBase<IsolatedFoundationSectionSize>;

export type FoundationSection = LinearFoundationSection | IsolatedFoundationSection;

export interface IntermediateExtrusionFrameInfo {
  readonly sourceDescriptorId: string;
  readonly halfEdgeIndex: number;
  readonly sectionId: string;
  readonly sectionIndex: number;
  readonly extrusionIndex: number;
  readonly sourceDirX: number;
  readonly sourceDirY: number;
  readonly sourceDirZ: number;
  readonly sourcePlane: {
    readonly originX: number;
    readonly originY: number;
    readonly originZ: number;
    readonly uX: number;
    readonly uY: number;
    readonly uZ: number;
    readonly vX: number;
    readonly vY: number;
    readonly vZ: number;
  };
}

/**
 * IntermediateFoundation* types represent the pre-extrusion generation phase.
 * They intentionally mirror the final Foundation* shape while replacing nested
 * `extrusions` with `extrusionDescriptors` in sections.  We have to do this because
 * we need to resolve overlaps between foundation systems before we can finalize the extrusions.
 */
export interface IntermediateFoundationExtrusionDescriptor extends FoundationExtrusionSize {
  readonly profile: PolyLine2Polygon;
  readonly includeInPlanProfiles: boolean;
  readonly frameInfo: IntermediateExtrusionFrameInfo;
}

interface IntermediateFoundationSectionBase<T extends FoundationSectionSize> {
  readonly id: string;
  readonly size: T;
  readonly planProfiles: PolyLine2[];
  readonly extrusionDescriptors: IntermediateFoundationExtrusionDescriptor[];
  readonly path: FoundationPath;
  readonly material: ExteriorMaterial;
}

export interface IntermediateLinearFoundationSection
  extends IntermediateFoundationSectionBase<LinearFoundationSectionSize> {
  readonly startCap: WallEndCap;
  readonly endCap: WallEndCap;
  readonly gapProfiles: PolyLine2[];
  readonly leftEdgeGaps: StartSortedWallGaps;
  readonly rightEdgeGaps: StartSortedWallGaps;
}

export type IntermediateIsolatedFoundationSection = IntermediateFoundationSectionBase<IsolatedFoundationSectionSize>;

export type IntermediateFoundationSection = IntermediateLinearFoundationSection | IntermediateIsolatedFoundationSection;

export interface IntermediateFoundationStrip {
  readonly id: string;
  readonly path: FoundationPath;
  readonly sections: IntermediateFoundationSection[];
}

export type HalfEdgeToIntermediateFoundationStrip = { [halfEdgeIndex: number]: IntermediateFoundationStrip };

interface IntermediateFoundationSubSystemBase<T extends FoundationSubSystemType> {
  readonly type: T;
  readonly strips: HalfEdgeToIntermediateFoundationStrip;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
}

type IntermediateMonolithicFoundationSubSystem =
  IntermediateFoundationSubSystemBase<FoundationSubSystemType.MONOLITHIC>;
type IntermediateStemWallCMUFoundationSubSystem =
  IntermediateFoundationSubSystemBase<FoundationSubSystemType.STEM_WALL_CMU>;
type IntermediateStemWallCIPFoundationSubSystem =
  IntermediateFoundationSubSystemBase<FoundationSubSystemType.STEM_WALL_CIP>;
type IntermediateIsolatedFoundationSubSystem = IntermediateFoundationSubSystemBase<FoundationSubSystemType.ISOLATED>;

export type IntermediateFoundationSubSystem =
  | IntermediateMonolithicFoundationSubSystem
  | IntermediateStemWallCMUFoundationSubSystem
  | IntermediateStemWallCIPFoundationSubSystem
  | IntermediateIsolatedFoundationSubSystem;

/**
 * foundation strips are sets of sections on a foundation half edge, which can be located at various
 * positions relative to the half edge in 3d space
 */
export interface FoundationStrip {
  readonly id: string;
  readonly path: FoundationPath;
  readonly sections: FoundationSection[];
}

export const FOUNDATION_ATTRIBUTE_CONDITION = "condition";
export const FOUNDATION_ATTRIBUTE_COMPONENT_TYPE = "componentType";
/** FoundationSubSystemType (MONOLITHIC, STEM_WALL_CMU, …) on stem wall analytic entities. */
export const FOUNDATION_ATTRIBUTE_STEM_WALL_SUBSYSTEM_TYPE = "foundationStemWallType";
